import React, { useState } from 'react';
import { Upload, CheckCircle, AlertCircle, Play, Database, FileText, Download } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const UniversalImporter = () => {
    const { t, i18n } = useTranslation();
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null); 
    const [mapping, setMapping] = useState({});
    const [targetEntity, setTargetEntity] = useState('ASSET'); 

    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    const entityMetadata = {
        ASSET: ['assetnum', 'description', 'site_id', 'org_id', 'assettype', 'status', 'parent', 'priority', 'serial', 'location_id', 'asset_tag', 'safetypnum', 'failure_class', 'purchase_price', 'replace_cost', 'install_date', 'warranty_exp_date', 'vendor', 'lat', 'lng', 'glaccount'],
        LOCATION: ['location_id', 'description', 'site_id', 'org_id', 'type', 'status', 'parent'],
        ITEM: ['itemnum', 'description', 'site_id', 'org_id', 'category', 'status', 'parent'],
        JP: ['jpnum', 'description', 'site_id', 'org_id', 'status', 'priority', 'reportdate', 'reportedby'],
        PM: ['pmnum', 'description', 'site_id', 'org_id', 'frequency', 'status', 'priority', 'nextduedate', 'jpnum', 'worktype', 'wostatus', 'priority', 'supervisor', 'glaccount', 'sotreroom', 'storeroom_site', 'assetnum', 'asset_loc'],
        PERSON: ['personid', 'firstname', 'lastname', 'email', 'phone', 'status', 'displayname'],
        LABOR: ['laborcode', 'description', 'craft_code', 'actual_rate', 'status', 'site_id', 'org_id', 'personid'],
        CRAFT: ['craftcode', 'description', 'org_id', 'site_id', 'standard_rate']
    };

    const handleDownloadTemplate = async () => {
        try {
            const response = await axios.get(`/api/import/download-template/${targetEntity}`, {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${targetEntity.toLowerCase()}_template.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success(t('importer.toasts.template_downloaded'));
        } catch (err) {
            toast.error(t('importer.toasts.template_fail'));
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        
        const loadId = toast.loading(t('importer.toasts.analyzing'));
        try {
            const res = await axios.post('/api/import/data-preview', formData);
            setPreview(res.data);
            setFile(file);
            
            const autoMap = {};
            res.data.columns.forEach(col => {
                const cleanCol = col.toLowerCase().trim();
                const match = entityMetadata[targetEntity].find(field => {
                    const cleanField = field.toLowerCase().trim();
                    return (
                        cleanField === cleanCol || 
                        cleanField === cleanCol.replace('as_', '') || 
                        cleanField === cleanCol.replace('_id', 'id') || 
                        cleanField === cleanCol.replace('id', '_id')
                    );
                });
                if (cleanCol === 'site_id') autoMap[col] = 'site_id';
                else if (cleanCol === 'org_id') autoMap[col] = 'org_id';
                else if (match) autoMap[col] = match;
            });
            setMapping(autoMap);
            toast.success(t('importer.toasts.preview_ready'), { id: loadId });
        } catch (err) {
            toast.error(t('importer.toasts.upload_fail'), { id: loadId });
        }
    };

    const runImport = async () => {
        if (!file) return toast.error(t('importer.toasts.no_file'));

        const loadId = toast.loading(t('importer.toasts.processing'));
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('file', file); 
            const config = { target_entity: targetEntity, mapping: mapping };
            formData.append('payload_str', JSON.stringify(config));

            await axios.post('/api/import/execute', formData, {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data' 
                }
            });

            toast.success(t('importer.toasts.import_complete'), { id: loadId });
            setPreview(null);
            setFile(null);
        } catch (err) { 
            const detail = err.response?.data?.detail || t('importer.toasts.import_fail');
            toast.error(detail, { id: loadId }); 
        }
    };

    return (
        <div className={`p-8 bg-gray-50 min-h-screen ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'} uppercase text-[11px] font-bold`}>
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-6xl mx-auto">
                <h2 className={`flex items-center gap-4 text-lg text-blue-900 mb-8 border-b pb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Database size={24}/> {t('importer.title')}
                </h2>

                {/* Step 1: Selection & Upload */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <div className="flex flex-col gap-4">
                        <div className={`flex justify-between items-end ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <label className="text-gray-400">{t('importer.target_table')}</label>
                            <button 
                                onClick={handleDownloadTemplate}
                                className="bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white px-4 py-2 rounded-md border border-blue-200 flex items-center gap-2 transition-all shadow-sm group"
                            >
                                <Download size={14} className="group-hover:animate-bounce"/> 
                                {t('importer.download_template')}
                            </button>
                        </div>
                        <select 
                            className={`p-3 border-2 rounded-md bg-gray-50 text-blue-900 outline-none focus:border-blue-500 transition-colors ${isRTL ? 'text-right' : 'text-left'}`}
                            value={targetEntity}
                            onChange={(e) => {
                                setTargetEntity(e.target.value);
                                setPreview(null);
                                setMapping({});
                            }}
                        >
                            <option value="ASSET">{t('apps.ASSET')}</option>
                            <option value="LOCATION">{t('apps.LOCATION')}</option>
                            <option value="ITEM">{t('apps.ITEMS')}</option>
                            <option value="JP">{t('menu.job_plans')}</option>
                            <option value="PM">{t('menu.pm_schedules')}</option>
                            <option value="PERSON">{t('menu.person')}</option>
                            <option value="LABOR">{t('menu.labor')}</option>
                            <option value="CRAFT">{t('menu.crafts')}</option>
                        </select>
                    </div>

                    <div className="border-2 border-dashed border-blue-200 rounded-lg p-6 flex flex-col items-center justify-center bg-blue-50/30 group hover:border-blue-500 transition-all cursor-pointer relative">
                        <Upload className="text-blue-400 mb-2 group-hover:scale-110 transition-transform" size={32}/>
                        <span className="text-blue-900 mb-2 text-center">{file ? file.name : t('importer.upload_instruction')}</span>
                        <input type="file" onChange={handleFileUpload} className="absolute inset-0 opacity-0 cursor-pointer" />
                    </div>
                </div>

                {/* Step 2: Smart Mapping & Preview */}
                {preview && (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <h3 className={`text-blue-700 mb-4 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            {t('importer.field_mapping')} <CheckCircle size={14} className="text-green-500"/>
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-md border max-h-60 overflow-y-auto custom-scrollbar mb-8">
                            {preview.columns.map(col => (
                                <div key={col} className={`flex flex-col gap-1 border-b pb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                                    <span className="text-gray-500 text-[9px]">{col} ({t('importer.excel')})</span>
                                    <select 
                                        className={`p-1.5 border rounded text-blue-800 bg-white outline-none focus:ring-1 focus:ring-blue-400 ${isRTL ? 'text-right' : 'text-left'}`}
                                        value={mapping[col] || ''}
                                        onChange={(e) => setMapping({...mapping, [col]: e.target.value})}
                                    >
                                        <option value="">-- {t('importer.ignore')} --</option>
                                        {entityMetadata[targetEntity].map(f => <option key={f} value={f}>{f}</option>)}
                                    </select>
                                </div>
                            ))}
                        </div>

                        <h3 className={`text-blue-700 mb-4 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            {t('importer.preview_title')} <FileText size={14}/>
                        </h3>
                        <div className="overflow-x-auto border rounded-md mb-8 shadow-inner">
                            <table className="w-full text-left text-[10px]">
                                <thead className="bg-slate-800 text-white">
                                    <tr className={isRTL ? 'flex-row-reverse' : ''}>
                                        {preview.columns.map(c => <th key={c} className={`p-2 border-r border-slate-700 ${isRTL ? 'text-right' : 'text-left'}`}>{c}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.preview_data.map((row, i) => (
                                        <tr key={i} className={`border-b hover:bg-yellow-50 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}>
                                            {preview.columns.map(c => <td key={c} className={`p-2 border-r ${isRTL ? 'text-right' : 'text-left'}`}>{row[c]}</td>)}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <button 
                            onClick={runImport}
                            className="w-full py-4 bg-green-600 text-white rounded-md font-black text-sm flex items-center justify-center gap-4 hover:bg-blue-900 transition-all shadow-lg transform active:scale-[0.99]"
                        >
                            <Play size={20} fill="white"/> {t('importer.execute_btn')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UniversalImporter;